/**
 * Resolves the other participant's profile GUID for a 1:1 conversation.
 *
 * Conversation list payloads often omit participants, so the GUID is looked up
 * in the conversation, then its members, then the participants API — the same
 * order mobile's chat details screen uses.
 */
import type { Conversation, ConversationMember } from '@wlb/shared/types/chat';

const pickPeer = (
  members: ConversationMember[] | undefined,
  selfUserId: string | null,
): ConversationMember | undefined =>
  (members ?? []).find(member => {
    if (!member?.profileUserGuid) return false;
    return !(selfUserId && String(member.id) === selfUserId);
  });

export const resolvePeerUserGuid = async (
  conversation: Conversation | null | undefined,
  selfUserId: string | null,
  getParticipants: (conversationId: string) => Promise<ConversationMember[]>,
): Promise<string | null> => {
  if (!conversation) return null;

  const known =
    conversation.peerUserGuid || pickPeer(conversation.members, selfUserId)?.profileUserGuid;
  if (known) return String(known);

  try {
    const participants = await getParticipants(conversation.id);
    return pickPeer(participants, selfUserId)?.profileUserGuid ?? null;
  } catch {
    return null;
  }
};
